import { autoinject, computedFrom, PLATFORM, useView } from 'aurelia-framework';
import { NavigationInstruction, RouteConfig } from 'aurelia-router';
import { AppRouteID } from '~/app-routes';
import { ContextMenuItem } from '~/components/context-menu-list/context-menu-list';
import {
	SearchFieldModel,
	SearchType,
} from '~/components/field/common/search-definition';
import { CommonURLParam } from '~/constants';
import ClassicForm from '~/forms/classic-form';
import { DataTypeEnum } from '~/models/data-type';
import { FormModel } from '~/models/form-model';
import { generateLinkNoCustomisationParam } from '~/utils/router-utils';

@autoinject
@useView(PLATFORM.moduleName('~/forms/classic-form.html'))
export class MemberDetails extends ClassicForm {
	public static readonly FORM_NAME = 'member-and-client-details';
	private params: { [key: string]: string };

	constructor(...rest) {
		super(
			MemberDetails.FORM_NAME,
			true, // save
			false, // delete
			true, // notes
			true, // audit
			false, // add
			2,
			['MDz_Fund', 'MDz_Member'],
			...rest,
		);
		this.addCustomisableAreaName = 'singleRecordDisplay';
		this.tableConfig.rowIdKey = 'MDi_Identity';
		this.tableConfig.defaultSortField = 'MDi_Identity';
		this.tableConfig.defaultSortDirection = 'asc';
		this.tableConfig.tableColumns = [
			{
				name: 'MDz_Fund',
				label: 'Fund',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'MDz_Member',
				label: 'Member',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'MDz_Payroll',
				label: 'Employer',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'MDz_Group',
				label: 'Group',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'MDz_Surname',
				label: 'Surname',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'MDz_Given_Names',
				label: 'Given Names',
				dataType: { name: DataTypeEnum.String },
			},
		];
		this.tableConfig.multiColumnOrder = [
			{ name: 'MDz_Fund', direction: 'asc' },
			{ name: 'MDz_Member', direction: 'asc' },
			{ name: 'MDz_Surname', direction: 'asc' },
			{ name: 'MDz_Given_Names', direction: 'asc' },
		];
		this.tableConfig.selectIfOnlyOne = true;
	}

	protected activate(
		params,
		routeConfig: RouteConfig,
		navigationInstruction: NavigationInstruction,
	) {
		this.params = params;
		return super.activate(params, routeConfig, navigationInstruction);
	}

	/**
	 * @override
	 */
	@computedFrom(
		'formModel',
		'formModel.fields.MDz_Fund.value',
		'formModel.fields.MDz_Member.value',
		'formModel.fields.MDz_Acct_No.value',
	)
	public get slideoutContextMenu(): ContextMenuItem[] {
		const params: { [key: string]: string } = { ...this.params };
		if (this.formModel && this.formModel.fields) {
			// if a member hasn't been selected then use it from selected record
			if (
				!params[CommonURLParam.FUND] &&
				this.formModel.fields.MDz_Fund
			) {
				params[CommonURLParam.FUND] =
					this.formModel.fields.MDz_Fund.value;
			}
			if (
				!params[CommonURLParam.MEMBER] &&
				this.formModel.fields.MDz_Member
			) {
				params[CommonURLParam.MEMBER] =
					this.formModel.fields.MDz_Member.value;
			}
			if (
				!params[CommonURLParam.CLIENT] &&
				this.formModel.fields.MDz_Acct_No
			) {
				params[CommonURLParam.CLIENT] =
					this.formModel.fields.MDz_Acct_No.value;
			}
		}
		return [
			{
				label: this.i18n.tr(
					'common-strings:route-title:personal-details',
				),
				iconClass: 'fa-external-link-alt',
				href: generateLinkNoCustomisationParam(
					AppRouteID.PersonalDetails,
					params,
				),
			},
		];
	}

	protected formModelChanged(formModel: FormModel, oldValue?: FormModel) {
		super.formModelChanged(formModel, oldValue);
		if (!formModel) {
			return;
		}
		let searchFieldModel: SearchFieldModel =
			formModel.fields.MDz_Reversion_Client;
		if (searchFieldModel) {
			searchFieldModel.searchType = SearchType.CLIENT;
		}

		searchFieldModel = formModel.fields.MDz_Target_Table;
		if (searchFieldModel) {
			searchFieldModel.searchType = SearchType.SCALE_TABLES;
			searchFieldModel.displayResultsOnFocus = true;
		}

		searchFieldModel = formModel.fields.MDz_Investment_Table;
		if (searchFieldModel) {
			searchFieldModel.searchType = SearchType.SCALE_TABLES;
			searchFieldModel.displayResultsOnFocus = true;
		}

		searchFieldModel = formModel.fields.MDz_Redemption_Table;
		if (searchFieldModel) {
			searchFieldModel.searchType = SearchType.SCALE_TABLES;
			searchFieldModel.displayResultsOnFocus = true;
		}

		searchFieldModel = formModel.fields.MDz_Spouse_Fund;
		if (searchFieldModel) {
			searchFieldModel.searchType = SearchType.FUND;
		}

		searchFieldModel = formModel.fields.MDz_Spouse;
		if (searchFieldModel) {
			searchFieldModel.searchType = SearchType.MEMBER;
			searchFieldModel.filterBy = [
				{ col: 'MDz_Spouse_Fund', targetCol: 'MDz_Fund' },
			];
		}
		searchFieldModel = formModel.fields.MDz_Group_Profile;
		if (searchFieldModel) {
			searchFieldModel.searchType = SearchType.GROUP_INVESTMENT_PROFILE;
			searchFieldModel.displayResultsOnFocus = true;
			searchFieldModel.filterBy = [
				{ col: 'MDz_Fund', targetCol: 'IPz_Fund' },
			];
		}
	}
}
